<template>
    <Teleport to="body">
        <transition name="consent-fade">
            <div v-if="visible" class="consent-overlay">
                <div class="consent-banner">
                    <div class="consent-logo">
                        <img
                            src="@/assets/images/sportspoule-logo.svg"
                            alt="Sportspoule"
                        />
                    </div>

                    <h2 class="consent-title">
                        {{ $t("consent.title") }}
                    </h2>

                    <p class="consent-description">
                        {{ $t("consent.description") }}
                    </p>

                    <div class="consent-checks">
                        <div class="consent-check">
                            <span class="consent-check-icon">&#10003;</span>
                            <span>{{ $t("consent.detail-anonymous") }}</span>
                        </div>
                        <div class="consent-check">
                            <span class="consent-check-icon">&#10003;</span>
                            <span>{{ $t("consent.detail-changeable") }}</span>
                        </div>
                    </div>

                    <div class="consent-actions">
                        <button class="btn consent-accept" @click="accept">
                            {{ $t("consent.accept") }}
                        </button>
                        <button class="btn consent-decline" @click="decline">
                            {{ $t("consent.decline") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script lang="ts" setup>
    import { ref } from "vue";
    import { useConsent } from "@/composables/useConsent";

    const { grantConsent, denyConsent } = useConsent();
    const visible = ref(true);

    async function accept() {
        await grantConsent();
        visible.value = false;
    }

    async function decline() {
        await denyConsent();
        visible.value = false;
    }
</script>

<style lang="scss" scoped>
    .consent-overlay {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: 8%;
        background: var(--modal-background-color);
    }

    .consent-banner {
        width: 100%;
        max-width: 320px;
        padding: 28px 32px;
        padding-bottom: max(24px, env(safe-area-inset-bottom));
        text-align: center;
    }

    .consent-logo {
        display: flex;
        justify-content: center;
        margin-bottom: 16px;

        img {
            width: 52px;
            height: 52px;
            border-radius: 14px;
        }
    }

    .consent-title {
        font-size: 21px;
        font-weight: 700;
        color: var(--color-black);
        margin: 0 0 6px 0;
        line-height: 1.3;
    }

    .consent-description {
        font-size: 15px;
        font-weight: 500;
        color: var(--color-grey-700);
        line-height: 1.5;
        margin: 0 auto 18px;
        max-width: 280px;
    }

    .consent-checks {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 32px;
    }

    .consent-check {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 500;
        color: var(--color-grey-700);
    }

    .consent-check-icon {
        color: var(--color-primary);
        font-weight: 700;
        font-size: 14px;
    }

    .consent-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .consent-accept {
        width: 100%;
        height: 52px;
        border: none;
        border-radius: 14px;
        background: var(--color-primary);
        color: var(--color-always-white);
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 64, 255, 0.25);
        transition: all 0.2s ease;

        &:active {
            transform: scale(0.97);
        }
    }

    .consent-decline {
        width: 100%;
        height: 48px;
        border: none;
        border-radius: 14px;
        background: transparent;
        color: var(--color-grey-600);
        opacity: 0.55;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: none;
        transition: all 0.2s ease;

        &:active {
            transform: scale(0.97);
        }
    }

    .consent-fade-enter-active {
        transition: opacity 0.3s ease;

        .consent-banner {
            transition:
                transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                opacity 0.3s ease;
        }
    }

    .consent-fade-leave-active {
        transition: opacity 0.25s ease;

        .consent-banner {
            transition:
                transform 0.25s ease,
                opacity 0.25s ease;
        }
    }

    .consent-fade-enter-from {
        opacity: 0;

        .consent-banner {
            transform: translateY(40px);
            opacity: 0;
        }
    }

    .consent-fade-leave-to {
        opacity: 0;

        .consent-banner {
            transform: translateY(20px);
            opacity: 0;
        }
    }
</style>
